import { CheckmarkIcon, CrossIcon, CurecallTooltip, Divider } from '@honestica/curecall-ui'
import { InteropLogProcessingInfoComponent } from './interop-log-processing-info.component'
import { InteropLogPayloadDetailComponent } from './interop-log-payload-detail.component'
import { EInteropLogSortOrder, TPatientInteropLog } from './interop-log.type'
import { useTranslation } from 'react-i18next'
import { InteropLogActionsComponent } from './interop-log-actions.component'
import { getHoursMinutesAndSeconds } from 'src/utils/date-formatter.util'
import { TPatient } from 'src/types/patient'
import { TProject } from 'src/types/project'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'
import TimezoneAwareTime from 'src/components/timezone-aware-time/timezone-aware-time.component'
import dayjs from 'dayjs'
import { twMerge } from 'src/utils/tw-merge.util'

function TimestampsForList({
  interopLog,
  timezone,
}: Readonly<{ interopLog: TPatientInteropLog; timezone?: string }>) {
  const generatedAt = interopLog.payload?.timestamp
  const receivedAt = interopLog.createdAt

  const receivedTime = <TimezoneAwareTime date={receivedAt} formatter="dateWithHours" displaySeconds />

  if (!generatedAt) {
    return <span>{receivedTime}</span>
  }
  const generatedTime = getHoursMinutesAndSeconds(interopLog.payload?.timestamp, timezone)
  const delay = dayjs(receivedAt).diff(dayjs(generatedAt), 'minutes')
  if (delay < 10) {
    return (
      <span>
        {receivedTime} (de {generatedTime})
      </span>
    )
  }
  const delayHours = Math.floor(delay / 60)
  const delayMinutes = String(delay % 60).padStart(2, '0')
  const pillColor = delay < 45 ? 'c-bg-orange-400' : 'c-bg-red-500'
  return (
    <span>
      {receivedTime} (de {generatedTime})
      <span className={twMerge(pillColor, 'c-text-white c-ml-1   c-px-2 c-py-1 c-rounded-full c-text-xs')}>
        {delayHours > 0 ? `${delayHours}h${delayMinutes}` : `${delayMinutes}m`}
      </span>
    </span>
  )
}

function getInteropLogType(interopLog: TPatientInteropLog) {
  const typeParts: string[] = []
  if (interopLog.payload?.messageType) {
    typeParts.push(`${interopLog.payload.messageType} -`)
  }
  if (interopLog.payload?.event) {
    typeParts.push(`${interopLog.payload.event}`)
  }
  return typeParts.join(' ')
}

function getEmissionDate(log: TPatientInteropLog) {
  return log.payload?.timestamp ?? log.createdAt
}

function groupLogsByDate(logs: TPatientInteropLog[], sortOrder: EInteropLogSortOrder) {
  const grouped = new Map<string, TPatientInteropLog[]>()

  for (const log of logs) {
    const groupDate = sortOrder === EInteropLogSortOrder.EMISSION ? getEmissionDate(log) : log.createdAt
    const dateKey = dayjs(groupDate).format('YYYY-MM-DD')
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, [])
    }
    grouped.get(dateKey)!.push(log)
  }

  return Array.from(grouped.entries()).sort(([dateKeyA], [dateKeyB]) => dateKeyB.localeCompare(dateKeyA))
}

export function InteropLogListComponent({
  patientInteropLogs,
  patient,
  project,
  sortOrder,
}: Readonly<{
  patientInteropLogs: TPatientInteropLog[]
  sortOrder: EInteropLogSortOrder
  patient: Pick<TPatient, 'id'>
  project: TProject
}>) {
  const { t } = useTranslation()
  const { displayTimezone } = useTimezonePreference()
  const groupedLogs = groupLogsByDate(patientInteropLogs, sortOrder)

  return (
    <>
      <div className="c-flex c-items-center">
        <span className="c-flex-1 c-whitespace-nowrap c-text-left">
          {t('components.patient.interop-logs.date-column')}
        </span>
        <span className="c-flex-1 c-whitespace-nowrap c-text-left">
          {t('components.patient.interop-logs.type-column')}
        </span>
        <span className="c-flex-1 c-whitespace-nowrap c-text-left">
          {t('components.patient.interop-logs.mapped')}
        </span>
      </div>

      {groupedLogs.map(([dateKey, logs], groupIndex) => (
        <div key={`date-group-${dateKey}`}>
          <div
            className={`c-bg-blue-200 c-rounded-2xl c-w-1/3 c-p-1 c-mb-2 c-font-semibold c-text-gray-700 c-text-center c-mx-auto ${
              groupIndex > 0 && 'c-mt-6'
            }`}
          >
            <TimezoneAwareTime date={dateKey} formatter="date" />
          </div>

          {logs.map((interopLog) => (
            <details
              key={`interop-log-${interopLog.id}`}
              className={twMerge(
                'c-border c-p-1 c-mb-2',
                interopLog.CompositeFromInteropLogId ? 'c-bg-blue-50' : 'c-bg-gray-50',
                interopLog.isLatest && 'c-border-l-4 c-border-l-blue-500',
              )}
            >
              <summary className="c-flex c-items-center">
                <span className="c-flex-1 c-whitespace-nowrap c-text-left">
                  <div className="c-flex-col c-items-center">
                    <div className="c-flex-1 c-whitespace-nowrap c-text-left">
                      <TimestampsForList interopLog={interopLog} timezone={displayTimezone} />
                      {interopLog.duplicateCount ? (
                        <span className="c-bg-blue-500 c-text-white c-ml-1   c-px-2 c-py-1 c-rounded-full c-text-xs">
                          +{interopLog.duplicateCount}
                        </span>
                      ) : null}
                    </div>
                    <div className="c-flex-1 c-whitespace-nowrap c-text-left">
                      {interopLog.payload?.consultation?.remoteId
                        ? `C: ${interopLog.payload?.consultation?.remoteId} `
                        : ''}
                      {interopLog.payload?.consultationsToRemove?.[0]
                        ? `C: ${interopLog.payload?.consultationsToRemove?.[0]} `
                        : ''}
                      {interopLog.payload?.visit?.number ? `V: ${interopLog.payload?.visit?.number}` : ''}
                    </div>
                  </div>
                </span>
                <span className="c-flex-1 c-whitespace-nowrap c-text-left">
                  {getInteropLogType(interopLog)}
                </span>
                <span className="c-flex-1 c-whitespace-nowrap c-text-center">
                  {interopLog.InteropMappingId > 0 && (
                    <div className="c-flex c-gap-2 c-align-middle">
                      <CheckmarkIcon className="c-text-green-500 c-h-3 c-w-3" />
                      <span>{interopLog.InteropMapping.description}</span>
                    </div>
                  )}

                  {interopLog.errors?.some(
                    (error) => error.error !== 'No mapping found for consultation data',
                  ) && (
                    <CurecallTooltip content={interopLog.errors?.[0]?.error}>
                      <CrossIcon className="c-text-red-500 c-h-3 c-w-3" />{' '}
                    </CurecallTooltip>
                  )}
                </span>
              </summary>
              <Divider />

              <InteropLogProcessingInfoComponent interopLog={interopLog} />
              <InteropLogActionsComponent interopLog={interopLog} patient={patient} project={project} />
              <InteropLogPayloadDetailComponent
                interopLogPayload={interopLog.payload}
              ></InteropLogPayloadDetailComponent>
            </details>
          ))}
        </div>
      ))}
    </>
  )
}
